// The current user's saved templates from user_text_templates, shared across views
// so the task detail quick-picks do not refetch per task.
//
// The table carries two types behind one endpoint: type 1 are the description
// quick-picks ("kiirvalikud") inserted into a task's notes2, type 2 are email body
// templates. The API returns them pre-split, so one request fills both lists.
//
// Deliberately memory-only, unlike usePerms which mirrors to localStorage:
// template bodies are personal free text, and logout does not clear localStorage
// today, so a persisted copy would outlive the session on a shared device. The
// cost is one small GET per app load.
//
// Seeded after login (LoginComponent) and, as a fallback for sessions that
// predate this feature or survived a reload, on TaskDetailView mount.

import { ref } from 'vue';
import axios from '@/services/axios';

const textTemplates = ref([]);
const emailTemplates = ref([]);
let loaded = false;
let loadPromise = null;

function applyPayload(payload) {
    textTemplates.value = payload?.text_templates || [];
    emailTemplates.value = payload?.email_templates || [];
}

export function loadTemplates(force = false) {
    if (!force && (loaded || loadPromise)) {
        // Concurrent callers share the in-flight request, as in utils/googleMaps.js.
        return loadPromise || Promise.resolve(textTemplates.value);
    }
    loadPromise = axios.get('/users/profile/text-templates')
        .then(response => {
            applyPayload(response.data.data);
            loaded = true;
            return textTemplates.value;
        })
        .catch(error => {
            // Never let this block a view: an empty list just hides the quick-pick.
            console.error('Error fetching text templates:', error);
            return textTemplates.value;
        })
        .finally(() => {
            loadPromise = null;
        });
    return loadPromise;
}

export function getTextTemplates() {
    return textTemplates.value;
}

export function getEmailTemplates() {
    return emailTemplates.value;
}

// Write-through from the profile page, so an edit there shows up in the picker
// without a refetch or a re-login.
export function setTextTemplates(list) {
    textTemplates.value = list || [];
    loaded = true;
}

export function setEmailTemplates(list) {
    emailTemplates.value = list || [];
    loaded = true;
}

export function clearTemplates() {
    textTemplates.value = [];
    emailTemplates.value = [];
    loaded = false;
    loadPromise = null;
}
